import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';
import { AppliedFilter, serializeFilters } from '../components/molecules/FilterBuilder';

const API = appConfig.apis.contractsManagementApiEnv;

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

export interface OperationResult {
    credit_operation_id: string;
    sequencial_number: number;
    operation_number: string;
    operation_type: string;
    credit_product_type_name?: string;
    status: string;
    approved_value: number;
    nominal_value: number | null;
    loan_amount: number | null;
    participants: any[];
    operation_dictionary?: any;
    amendment_number?: number;
    amendment_reason?: string | null;
    expiration_date?: string | null;
    with_recourse?: boolean;
    originator?: string | null;
    billed?: boolean;
    created_by?: string;
}

export interface OperationListPage {
    list: OperationResult[];
    page: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
}

/**
 * Credor "dono" da operação. Carregado de GET /creditors do contracts-management.
 */
export interface Creditor {
    id: number;
    name: string;
    govTaxId: string;
    personId: string;
    type: string;
}

/**
 * Subconjunto do credor persistido no payload da operação (chave `creditor`).
 */
export interface CreditorPayload {
    name: string;
    govTaxId: string;
    personId: string;
    type: string;
}

/**
 * Filtros fixos da consulta de operações "pagáveis": só contrato finalizado e já
 * faturado (sem faturamento não existe invoice para receber).
 */
export const PAYABLE_OPERATION_FILTERS: Record<string, string> = {
    'filter[status]': 'Finalizado com sucesso',
    'filter[billed]': 'true',
};

export const contractsManagementApi = {
    /**
     * Listagem unificada de operações (cessões, empréstimos, estruturadas, confissões).
     * Fonte: credit.vw_list_operations. Aceita os filtros do FilterBuilder.
     */
    listOperations: async (options: {
        filters?: AppliedFilter[];
        page?: number;
        pageSize?: number;
        sortField?: string;
        sortOrder?: 'ASC' | 'DESC';
        fixedFilters?: Record<string, string>;
    } = {}): Promise<OperationListPage> => {
        const { filters = [], page = 0, pageSize = 20, sortField = 'sequencial_number', sortOrder = 'DESC', fixedFilters } = options;
        const params = serializeFilters(filters);
        Object.entries(fixedFilters || {}).forEach(([key, value]) => params.set(key, value));
        params.set('page', String(page));
        params.set('pageSize', String(pageSize));
        params.set('sortField', sortField);
        params.set('sortOrder', sortOrder);

        // URLSearchParams codifica espaço como "+", e o API Gateway não converte "+"
        // de volta para espaço — valores como "Finalizado com sucesso" precisam de %20.
        const queryString = params.toString().replace(/\+/g, '%20');

        const res = await fetch(
            `${API.address}/contracts/receivables_assignment/list?${queryString}`,
            { headers: await getHeaders() }
        );
        if (!res.ok) {
            const errData = await res.json().catch(() => null);
            throw new Error(errData?.message || `Erro ao buscar operações: status ${res.status}`);
        }
        const data = await res.json();
        return {
            list: data.list || [],
            page: data.page ?? page,
            pageSize: data.pageSize ?? pageSize,
            totalElements: data.totalElements ?? (data.list || []).length,
            totalPages: data.totalPages ?? 1,
        };
    },

    /** Busca operações de cessão de recebíveis por termo de pesquisa */
    searchReceivableAssignments: async (search: string): Promise<OperationResult[]> => {
        if (!search || search.length < 2) return [];
        const res = await fetch(
            `${API.address}/contracts/receivables_assignment/list?search=${encodeURIComponent(search)}&page=0&pageSize=10`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return [];
        const data = await res.json();
        return data.list || [];
    },

    /** Busca uma operação de cessão de recebíveis pelo ID */
    getReceivableAssignmentById: async (operationId: string): Promise<OperationResult | null> => {
        const res = await fetch(
            `${API.address}/contracts/receivables_assignment/${operationId}`,
            { headers: await getHeaders() }
        );
        if (res.status === 404) return null;
        if (!res.ok) throw new Error(`Erro ao buscar operação: ${res.status}`);
        const json = await res.json();
        const data = json.data || json;
        if (!data.credit_operation_id) throw new Error('Resposta inesperada da API de operações');
        return data;
    },

    /**
     * Lista os credores disponíveis para serem donos de uma operação.
     * Fonte: GET /contracts/creditors do contracts-management. Envelope: { data: [...] }.
     */
    listCreditors: async (): Promise<Creditor[]> => {
        const res = await fetch(
            `${API.address}/contracts/creditors`,
            { headers: await getHeaders() }
        );
        if (!res.ok) {
            const errData = await res.json().catch(() => null);
            throw new Error(errData?.message || `Erro ao buscar credores: status ${res.status}`);
        }
        const data = await res.json();
        return data.data || [];
    },

    /**
     * Remove (soft-delete) um título extraído de Risco Sacado, com justificativa obrigatória.
     * AF-4108. O título permanece na lista, marcado como removido.
     */
    removeUnderlyingReceivable: async (
        creditOperationId: string,
        receivableId: string,
        reason: string
    ): Promise<{ creditOperationId: string; receivableId: string; removed: boolean; removedReason: string; removedBy: string; removedAt: string }> => {
        const res = await fetch(
            `${API.address}/contracts/operations/${creditOperationId}/underlying-receivables/${receivableId}`,
            {
                method: 'PATCH',
                headers: await getHeaders(),
                body: JSON.stringify({ reason }),
            }
        );
        if (!res.ok) {
            const errData = await res.json().catch(() => null);
            throw new Error(errData?.message || `Erro ao remover título: status ${res.status}`);
        }
        const data = await res.json();
        return data.data || data;
    },
};
